import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStyles } from "../../../../../data/DataStyles";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { LoadingCircularProgress } from "../../../../../Loading";
import {
  a11yProps,
  CustomTabPanel,
  ManagingExportOptions,
} from "./ExportModalExtraComponets";
import { useDispatch, useSelector } from "react-redux";

import {
  initialState,
  resetExportState,
  setColumnsToExport,
  setEmailExport,
  setExportModalVisible,
} from "../../../../../../redux/slices/dataTable/dataExport";

import { ThunkDispatch } from "@reduxjs/toolkit";
import validator from "validator";
import { ColumnsDataTable } from "../../../../../../redux/actions/dataTable/DataTableTypes";
import { exportData } from "../../../../../../redux/actions/dataTable/DataFiltersAsyncActions";
import { selectExportDataState } from "../../../../../../redux/selectors/unifiedSelectors";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    setExportModalValues: (
      visible: boolean,
      columnsToExport: ColumnsDataTable[],
      filtersToExport: any
    ) => {
      dispatch(setExportModalVisible(visible));
      dispatch(setColumnsToExport({ columnsToExport, filtersToExport }));
    },

    closeModal: () => {
      dispatch(resetExportState());
    },

    setEmailExport: (email: string) => {
      dispatch(setEmailExport(email));
    },

    exportData: () => {
      dispatch(exportData());
    },
  };
};

export const ExportModal = () => {
  const [t] = useTranslation("global");
  const { setExportModalValues, setEmailExport, exportData, closeModal } =
    SetActions();

  const classes = useStyles();
  const emailList = JSON.parse(localStorage.getItem("emails") || "[]") || [];

  const {
    loadingExport,
    email,
    successExport,
    columns,
    columnsVisibility,
    dataDateActivity,
    filterActions,
    filtersSpecies,
    filterUsers,
    filtersLocation,
  } = useSelector(selectExportDataState);

  const columnsToExportFiltered = columns?.reduce(
    (acc: any, column: ColumnsDataTable) => {
      if (columnsVisibility?.[column.field] && column.field !== "id") {
        acc.push({
          name: column.field,
          columnType: column.columnType,
          localizedName: column.headerName,
          hidden: false,
        });
      }
      return acc;
    },
    []
  );

  const columnsToExportAll = columns
    ?.filter((column: ColumnsDataTable) => column.field !== "id")
    .map((column: ColumnsDataTable) => ({
      name: column.field,
      columnType: column.columnType,
      localizedName: column.headerName,
      hidden: false,
    }));

  const [value, setValue] = useState(1);

  const addEmailsToCache = (e: { email: string }) => {
    const newItem = e;
    const findEmail = emailList.find(({ email }) => email === newItem.email);
    if (!findEmail) {
      localStorage.setItem(
        "emails",
        JSON.stringify([...emailList, newItem] as any)
      );
    }
  };

  const handleChangeTabs = (event: React.SyntheticEvent, index: number) => {
    const filteredData = index === 0;
    const alldData = index === 1;

    const filtersToExport = {
      datesRange: {
        fromDate: dataDateActivity[0],
        toDate: dataDateActivity[1],
      },
      cropsTextIds: filteredData
        ? filtersSpecies
        : initialState.filtersToExport.cropsTextIds,
      scenarioNamesTextIds: filteredData
        ? filterActions
        : initialState.filtersToExport.scenarioNamesTextIds,
      usernames: filteredData
        ? filterUsers
        : initialState.filtersToExport.usernames,
      geoPointNames: filteredData
        ? filtersLocation
        : initialState.filtersToExport.geoPointNames,
    };

    setValue(index);

    if (filteredData) {
      setExportModalValues(true, columnsToExportFiltered, filtersToExport);
    } else if (alldData) {
      setExportModalValues(
        true,
        columnsToExportAll as unknown as ColumnsDataTable[],
        filtersToExport
      );
    }
  };

  const submitHandler = (event: React.SyntheticEvent<EventTarget>) => {
    event.preventDefault();
    exportData();
    addEmailsToCache({ email });
  };

  const typeOfExportSelected = ["filtered_data", "all_data"];

  const ComboBox = () => {
    return (
      <Autocomplete
        disablePortal
        id="auto-complete-email"
        options={emailList.map((option: any) => option?.email)}
        sx={{ width: "100%" }}
        freeSolo
        disableClearable
        onChange={(e: any) => {
          setEmailExport(e.target.innerText);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{ width: "100%", marginBottom: "30px" }}
            type="email"
            InputProps={{
              ...params.InputProps,
            }}
            label={t("Fleet_overview.overview_panel.createNewPosition.email")}
            onChange={(e) => {
              setEmailExport(e.target.value);
            }}
          />
        )}
      />
    );
  };

  return (
    <div className={classes.exportModal}>
      <div className="export-title">
        <div>{t("Data.Export_data")}</div>
        <div className="divider-export-modal">
          <Divider variant="fullWidth" sx={{ borderStyle: "none" }} />
        </div>
      </div>
      {!successExport ? (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={value} onChange={handleChangeTabs} centered>
              <Tab
                disabled
                sx={{ textTransform: "none", width: "50%", fontSize: "14px" }}
                label={t("Data.Filtered_data")}
                {...a11yProps(0)}
              />
              <Tab
                sx={{ textTransform: "none", width: "50%", fontSize: "14px" }}
                label={t("Data.All_data")}
                {...a11yProps(1)}
              />
            </Tabs>
          </Box>

          {typeOfExportSelected.map((type, index) => (
            <CustomTabPanel key={index} value={value} index={index}>
              <form onSubmit={submitHandler}>
                <div>
                  {type === "filtered_data" ? (
                    <div className="data-export-info">
                      {t("Data.Export_info")}
                    </div>
                  ) : (
                    <div className="data-export-info-all">
                      {t("Data.All_data_info")}
                    </div>
                  )}
                  <div className="data-filtered-first-section-styles"></div>
                  <div className="data-filtered-second-section-styles">
                    <div className="data-filtered-second-section-styles-email">
                      {t("Data.Send_by_email")}
                    </div>
                    <div className="data-filtered-second-section-styles-email-container">
                      {ComboBox()}
                    </div>
                  </div>
                  <div className="position-buttons-export">
                    {!loadingExport ? (
                      <Button
                        sx={{ textTransform: "none" }}
                        className="btn-styles mr-3"
                        variant="outlined"
                        onClick={closeModal}
                      >
                        {t(
                          "Fleet_overview.overview_panel.createNewPosition.cancel"
                        )}
                      </Button>
                    ) : (
                      ""
                    )}
                    {!loadingExport ? (
                      <Button
                        sx={{ textTransform: "none" }}
                        variant="contained"
                        size="large"
                        className="btn-styles"
                        disabled={
                          loadingExport || validator.isEmail(email) === false
                        }
                        type="submit"
                      >
                        {t("Common_options.Send")}
                      </Button>
                    ) : (
                      <LoadingCircularProgress />
                    )}
                  </div>
                </div>
              </form>
            </CustomTabPanel>
          ))}
        </Box>
      ) : (
        <ManagingExportOptions
          closeModal={closeModal}
          successExport={successExport}
        />
      )}
    </div>
  );
};
